import * as Sentry from '@sentry/vue'
import { mergeAcquisitionLogs, readAcquisitionCookie } from '@/utils/segment.utils'

// Reconciles the anonymous acquisition signal log (ahaSegment cookie, written
// by GTM on the landing site) into personalInformation.acquisition.
// AHA-43253 shipped this as a one-shot flush that skipped whenever the
// profile already held an acquisition value, so a profile saved with early
// junk could never converge on the real first touch (AHA-47419). It now
// merges the cookie into the persisted log (TTL-pruned, first touch wins)
// and patches only when the merged content differs from what is stored.
// Watched paths:
// - setCurrentUserAction: interactive login (LoginV3) and signup (RegisterV3;
//   also the RegisterV2 guest conversion and MSTeams config flows, where the
//   guards below make this a no-op).
// - getCurrentUserAction: app load with canUseCacheProfileOnLoad OFF.
// - loadingPage/fetchAndSaveCurrentUserAction: app load with the flag ON
//   (App.vue goes through loadingPage/getCurrentUserWithCacheAction, which
//   never dispatches getCurrentUserAction), so returning users still flush on
//   reload. When it is reached through getCurrentUserAction's internal cache
//   branch both hooks fire; the guards below keep the patch single.
const WATCHED_ACTIONS = [
  'getCurrentUserAction',
  'setCurrentUserAction',
  'loadingPage/fetchAndSaveCurrentUserAction',
]

// Order-sensitive content identity of a log; used to decide whether a patch
// would change anything.
const signalsKey = (log) => JSON.stringify(((log && log.signals) || []).map((s) => [s.t, s.v, s.at]))

const acquisitionFlushPlugin = (store) => {
  // In-flight latch: prevents a double flush when a watched action fires again
  // before the first PATCH resolves (the content comparison would still read
  // stale state at that point).
  let flushPromise = null
  // Content latch: patchCurrentUserAction commits the payload back into
  // currentUser, but if that state is stale or raced, this stops the same
  // content from being re-sent for the same user.
  let lastFlushedKey = null

  store.subscribeAction({
    after: async (action) => {
      if (!WATCHED_ACTIONS.includes(action.type)) return
      try {
        if (!store.getters['featureToggles/canUseAcquisitionSignalLog']) return
        const { currentUser } = store.state
        if (!currentUser?.id || currentUser.isGuest) return
        const persisted = currentUser.personalInformation?.acquisition
        const merged = mergeAcquisitionLogs(persisted, readAcquisitionCookie())
        if (!merged) return
        if (signalsKey(persisted) === signalsKey(merged)) return
        const mergedKey = `${currentUser.id}:${signalsKey(merged)}`
        if (lastFlushedKey === mergedKey) return
        if (flushPromise) return
        // Send only the acquisition delta; the backend merges
        // personalInformation per key, so concurrent writers of other keys
        // are not clobbered.
        flushPromise = store
          .dispatch('patchCurrentUserAction', {
            userId: currentUser.id,
            personalInformation: { acquisition: merged },
          })
          .then(() => {
            lastFlushedKey = mergedKey
          })
          .finally(() => {
            flushPromise = null
          })
        await flushPromise
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            context: 'acquisition-flush-plugin',
            userId: store.state.currentUser?.id,
            actionType: action.type,
          },
        })
      }
    },
  })
}

export default acquisitionFlushPlugin
